import { Component } from '@angular/core';
import { MessageService, MenuItem } from 'primeng/api';
import { ConfigService, AppDataService, TourOperatorService } from 'app/services';
import { AppComponent } from 'app/app.component';
import { TableLazyLoadEvent } from 'primeng/table';
import { OperatorRegistration } from 'app/experience/models';
import {
    OperatorActionModalComponent
} from 'app/experience/pages/operators/operator-action-modal/operator-action-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { OperatorActionType } from 'app/shared/enums/operator-action-type.enum';

interface OperatorsQuery {
    skip: number;
    take: number;
    search: string;
    sortField: string | string[];
    sortOrder: number;
}
interface ColumnDef {
    field: string;
    header: string;
    width?: string;
    disableSort?: boolean;
}

@Component({
    selector: 'tour-operators',
    templateUrl: './tour-operators.component.html',
    styleUrls: ['./tour-operators.component.scss'],
    providers: [MessageService]
})

export class TourOperatorsComponent {
    query: OperatorsQuery = {
        skip: 0,
        take: 12,
        search: '',
        sortField: 'OperatorId',
        sortOrder: -1
    };

    public breadcrumbs: MenuItem[] = [{ icon: 'icon_back_office' }, { label: 'Application' }, { label: 'Tour Operators' }];
    public loading: boolean = false;
    private searchTimer: number = 0;
    public rowsPerPageOptions: Array<number> = [12, 25, 50, 100];
    public totalRecords: number = -1;
    public operators: OperatorRegistration[] = [];

    public cols: ColumnDef[] = [
        { field: 'OperatorId', header: 'Operator ID', width: '90px' },
        { field: 'Name', header: 'Name' },
        { field: 'ManagerName', header: 'Xpress Agent' },
        { field: 'SubmittedDate', header: 'Submitted Date', width: '200px' },
        { field: 'DocumentsCount', header: 'Documents', width: '200px', disableSort: true },
        { field: 'ExpiryDate', header: 'Expiry Date', width: '200px' },
        { field: 'Status', header: 'Status' },
        { field: 'Action', header: 'Action', disableSort: true }
    ];

    constructor(
        private messageService: MessageService,
        private operatorsService: TourOperatorService,
        public configService: ConfigService,
        public app: AppComponent,
        public appData: AppDataService,
        public dialog: MatDialog)
    {}

    public handleLazyLoad(event: TableLazyLoadEvent): void {
        this.query.skip = event.first ?? 0;
        this.query.take = event.rows ?? this.query.take;

        if (event.sortField) {
            this.query.sortField = event.sortField;
            this.query.sortOrder = event.sortOrder ?? 1;
        }

        this.getOperators();
    }

    isBtnDisabled(row: any, action: OperatorActionType) {
        return (row.Status === 'Verified' && action === OperatorActionType.Approve) || (row.Status === 'Rejected' && action === OperatorActionType.Reject);
    }

    showActionModal(row: any, action: OperatorActionType) {
        if (this.isBtnDisabled(row, action)) {
            return;
        }

        const dto = {
            Operator: row,
            OperatorActionType: action
        };

        this.dialog.open(OperatorActionModalComponent, {
            width: '685px',
            maxWidth: '90vw',
            data: dto,
            disableClose: false,
            panelClass: 'scroll-dialog'
        })
            .afterClosed().subscribe((result) => {
            if (result) {
                this.getOperators();
            }
        });
    }

    getOperators() {
        this.loading = true;

        const dto = {
            Skip: this.query.skip,
            Take: this.query.take,
            SearchName: this.query.search,
            SortField: this.query.sortField,
            SortOrder: this.query.sortOrder
        };

        this.operatorsService.getOperators(dto, this.messageService)
            .then(data => {
                if (!data?.Success) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: data?.ErrorMessage
                    });
                    return;
                }

                this.operators = data.Items;
                this.totalRecords = data.TotalRecords;
            })
            .finally(() => this.loading = false);
    }

    handleFilterChange(): void {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer);
        }

        this.searchTimer = window.setTimeout(() => {
            this.query.skip = 0;
            this.getOperators();
            this.searchTimer = 0;
        }, 1500);
    }

    protected readonly OperatorActionType = OperatorActionType;
}
